import { Form, Field } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useState } from "react";
import Button from "@components/Button";
import InputCheckbox from "@components/Form/Checkbox";
import InputText from "@components/Form/InputText";
import Select from "@components/Form/Select";
import MultipleSelect from "@components/Form/CheckboxList";
import { leadCollectorApi } from "@lib/api";
import {
  composeFormValidators,
  isRequired,
  hasMinLength,
  hasMaxLength,
  isURL,
  isEmail,
} from "@lib/validations";

const countries = [
  "",
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Republic of Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
];

const categories = [
  {
    label: "Racire, Purificare, Incalzire",
    name: "aer-conditionat-climatizare",
  },
  {
    label: "Curatenie, Intretinere casa",
    name: "curatenie-intretinere-casa",
  },
  {
    label: "Bauturi, Cafea, Petshop",
    name: "apa-espresso-cafea-suc",
  },
  {
    label: "Cosmetice",
    name: "cosmetice-ingrijire-personala",
  },
  { label: "Copii, Jucarii, Bebe", name: "bebelusi-copii-jucarii" },
  { label: "Bricolaj, Sanitare, Mobila", name: "casa-iluminat-bricolaj" },
  {
    label: "Anvelope, Electronica Auto",
    name: "auto-moto-anvelope",
  },
  {
    label: "Trotinete, Biciclete, Sport, Voiaj",
    name: "trotinete-biciclete-sport",
  },
];

const SubscribeForm = ({ user, baseUrl, formId }) => {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState();
  const [currentCountry, setCurrentCountry] = useState("");
  const { isInitial, customer } = user;

  const handleContactSubmit = async (values) => {
    const { status, originalError } = await leadCollectorApi(values);
    if (status === 200) setSent(true);
    else setError(originalError);
  };

  return (
    <div className="flex flex-col items-start justify-start w-full">
      <Form
        onSubmit={(values) => handleContactSubmit(values)}
        initialValues={{
          //! That double render will return `undefined undefined` just into the
          //! first field `name` of this object if using the ternary conditional on it
          //! Until the validation reaches the `phone` key the second render already
          //! with the correct user information. THAT DOUBLE RENDER IS NOT NORMAL!
          fc_target: formId,
          name: `${customer?.first_name || ""} ${customer?.last_name || ""}`,
          phone: !isInitial ? customer?.telephone : "",
          email: !isInitial ? customer?.email : "",
        }}
        render={({ handleSubmit, submitting, pristine, invalid }) => (
          <form
            className="flex flex-col space-y-5 w-full"
            onSubmit={handleSubmit}
          >
            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="company"
                label="Numele companiei"
                type="text"
                component={InputText}
                required
                validate={composeFormValidators(isRequired(), hasMaxLength(60))}
              />
            </div>

            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="country"
                label="Tara"
                type="select"
                component={Select}
                required
                validate={composeFormValidators(isRequired())}
              >
                {countries.map((country, key) => (
                  <option key={key} value={country}>
                    {country}
                  </option>
                ))}
              </Field>

              <OnChange name="country">
                {(value) => {
                  setCurrentCountry(value);
                }}
              </OnChange>
            </div>

            {currentCountry === "Romania" && (
              <div className="w-full">
                <Field
                  className="!w-full md:!w-5/6 !max-w-full"
                  name="commercial_register_number"
                  label="Numar Registrul Comertului"
                  type="text"
                  component={InputText}
                  required
                  validate={composeFormValidators(isRequired())}
                />
              </div>
            )}

            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="name"
                label="Persoana de contact"
                type="text"
                component={InputText}
                required
                validate={composeFormValidators(
                  isRequired(),
                  hasMinLength(3),
                  hasMaxLength(100)
                )}
              />
            </div>

            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="phone"
                label="Numar de telefon"
                type="tel"
                component={InputText}
                required
                validate={composeFormValidators(isRequired())}
              />
            </div>

            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="email"
                label="Adresa de email"
                type="email"
                component={InputText}
                required
                validate={composeFormValidators(isRequired(), isEmail())}
              />
            </div>

            <div className="w-full">
              <Field
                className="!w-full md:!w-5/6 !max-w-full"
                name="website"
                label="Website"
                type="text"
                component={InputText}
                validate={composeFormValidators(isURL(), hasMaxLength(80))}
              />
            </div>

            <div className="w-full md:w-5/6">
              <MultipleSelect
                items={categories}
                title="Vreau sa vand produse in categoria"
                name="category"
                validate={composeFormValidators(isRequired())}
                required
              />
            </div>

            <div className="flex flex-col space-y-2 w-full">
              <Field
                className="!w-full md:!w-5/6 text-sm !max-w-full"
                name="terms_and_services"
                label={
                  <>
                    <span className="inline-block font-medium">Accept</span>
                    {` `}
                    <a
                      href={`${baseUrl}/suport-clienti/informatii-generale-termeni-si-conditii/`}
                      title="Termenii si Conditiile"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-red-lightdark"
                    >
                      Termenii si conditiile Altex Marketplace
                    </a>
                  </>
                }
                type="checkbox"
                component={InputCheckbox}
                required
                validate={composeFormValidators(isRequired())}
              />

              <Field
                className="!w-full md:!w-5/6 text-sm !max-w-full"
                name="gdpr"
                label="Accept politica GDPR"
                type="checkbox"
                component={InputCheckbox}
                required
                validate={composeFormValidators(isRequired())}
              />
            </div>

            {sent && (
              <div
                className={`flex items-center justify-between w-full md:w-5/6 px-3.5 py-3 rounded-tr-lg rounded-bl-lg ${
                  error ? "bg-alertRed-bg/25" : "bg-[#62D040]/25"
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    error ? "text-alertRed" : "text-[#319D10]"
                  }`}
                >
                  {error
                    ? "Cererea nu a putut fi inregistrata. Incercati din nou."
                    : "Cererea a fost inregistrata cu succes!"}
                </p>

                <div
                  className={`flex items-center justify-center rounded-full w-4 h-4 ${
                    error ? "bg-alertRed" : "bg-[#319D10]"
                  } cursor-pointer`}
                  onClick={() => setSent(false)}
                >
                  <svg className="w-2 h-2" version="1.1" viewBox="0 0 512 512">
                    <polygon
                      fill="#FFFFFF"
                      points="512,59.076 452.922,0 256,196.922 59.076,0 0,59.076 196.922,256 0,452.922 59.076,512 256,315.076 452.922,512 512,452.922 315.076,256"
                    />
                  </svg>
                </div>
              </div>
            )}

            <div className="w-full md:w-44">
              <Button
                title="Inscrie-te acum"
                type="submit"
                display="block"
                isOrderAgain={true}
                disabled={submitting || pristine || invalid}
                variant="secondary"
                hasMinWidth={false}
              />
            </div>
          </form>
        )}
      />

      <div className="text-[14px] mb-[4px] mt-[25px]">
        Ai deja cont? Logheaza-te in platforma aici!
      </div>

      <div className="w-full md:w-44">
        <Button
          title="Logheaza-te in cont"
          display="block"
          isOrderAgain={true}
          variant="secondary"
          hasMinWidth={true}
          href="https://marketplace.altex.ro/admin"
        />
      </div>
    </div>
  );
};

export default SubscribeForm;
